import React, { useContext, useState } from 'react';
import EditText from '../crm/shared/field_inputs/text/EditText';
import EditItem from '../crm/shared/field_inputs/item/EditItem';
import ExlEditNumber from '../exl/field_inputs/number/ExlEditNumber';
import { GetCourseById, HandleAddCourse } from './requests/CourseRequests';
import { DataContext as SharedDataContext } from '../../stores/crm-store';
import { createGetSuggestionsFromApiUrl } from '../crm/shared/field_inputs/HelpMethods';
import { navigate } from 'hookrouter';
import EditSelect from '../crm/shared/field_inputs/select/EditSelect';
import { courseCategoryOptions, courseFormatOptions, courseLanguageOptions, courseStatusOptions, getOptionsObjectValue, getOptionsObjectValues } from './shared/translations';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import ExlCourseFormModal from './shared/ExlCourseFormModal';
import ExlEditItemList from './field_inputs/item/ExlEditItemList';
import ExlEditDraft from './field_inputs/ExlEditDraft';
import Checkbox from '../Checkbox';
import { useTranslation } from 'react-i18next';
import { ADD_COURSE_BUTTON_LABEL, ADD_COURSE_LABEL, ADD_COURSE_TEMPLATE_LABEL, EMAIL_NOTIFICATIONS_FALSE, EMAIL_NOTIFICATIONS_TRUE } from '../../i18n/keyConstants';
import { COURSE_MODALS_ADDRESS_LABEL, COURSE_MODALS_BOOK_EXCEEDS_MAX_NUM_VALI, COURSE_MODALS_CATEGORY_LABEL, COURSE_MODALS_DESCRIPTION_LABEL, COURSE_MODALS_EMAIL_NOTIF_LABEL, COURSE_MODALS_FORMAT_LABEL, COURSE_MODALS_LABEL, COURSE_MODALS_LANG_LABEL, COURSE_MODALS_MAX_BOOKINGS_LABEL, COURSE_MODALS_MAX_RESERVES_LABEL, COURSE_MODALS_MEET_LINK_LABEL, COURSE_MODALS_NAME_LABEL, COURSE_MODALS_NO_ADDRESS_VALI, COURSE_MODALS_NO_CATE_VALI, COURSE_MODALS_NO_FORMAT_VALI, COURSE_MODALS_NO_LANG_VALI, COURSE_MODALS_NO_MAX_BOOK_VALI, COURSE_MODALS_NO_NAME_VALI, COURSE_MODALS_NO_STATUS_VALI, COURSE_MODALS_NO_TEACHER_VALI, COURSE_MODALS_RES_EXCEEDS_MAX_NUM_VALI, COURSE_MODALS_STATUS_LABEL, COURSE_MODALS_TEACHER_LABEL, COURSE_MODALS_TO_HIGH_MAX_BOOK_VALI, COURSE_MODALS_TO_HIGH_MAX_RES_VALI, COURSE_MODALS_TO_LONG_NAME_VALI, COURSE_MODALS_TO_LOW_MAX_BOOK_VALI, EDIT_COURSE_LABEL, COURSE_MODALS_PRICE_LABEL, COURSE_MODALS_TRAVEL_SUPPLEMENT_LABEL } from '../../i18n/keyConstants';
import { courseCategoryComponentsFilter, courseFormatComponentsFilter, courseDeterminePriceAndTravelSupplement } from './CourseHelperFunctions';

let defaultFormValues = {
    courseFormat: courseFormatOptions.Digital
}

const AddCourse = () => {
    const { dispatch: sharedDispatch } = useContext(SharedDataContext);
    const [defaultTemplate, setDefaultTemplate] = useState(defaultFormValues);
    const courseStatuses = getOptionsObjectValues(courseStatusOptions);
    const courseCategories = getOptionsObjectValues(courseCategoryOptions);
    const courseLanguages = getOptionsObjectValues(courseLanguageOptions);
    const courseFormats = getOptionsObjectValues(courseFormatOptions);
    const { t } = useTranslation(['courseModals', 'addCourse']);

    let label = t(ADD_COURSE_BUTTON_LABEL, {ns: 'addCourse'});

    const clearForm = () => {
        defaultFormValues = {
            courseFormat: courseFormatOptions.Digital
        }
        setDefaultTemplate(defaultFormValues);
    }

    async function setTemplate(value) {
        var course = await GetCourseById(value.id);
        if (course !== undefined) {
            defaultFormValues = {
                name: course?.name,
                teachers: course?.teachers,
                description: course?.description,
                maxNumBookings: course?.maxNumBookings,
                maxNumReserves: course?.maxNumReserves,
                status: getOptionsObjectValue(courseStatusOptions, course.status),
                category: getOptionsObjectValue(courseCategoryOptions, course.category),
                language: getOptionsObjectValue(courseLanguageOptions, course.language),
                location: course?.location,
                courseFormat: getOptionsObjectValue(courseFormatOptions, course.courseFormat),
                meetingLink: course?.meetingLink,
                price: course.price,
                travelSupplement: course.travelSupplement,
            };
            setDefaultTemplate(defaultFormValues);
        }
        else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    const formComponents = [
        {
            propName: 'courseTemplate',
            label: t(ADD_COURSE_TEMPLATE_LABEL, { ns: 'addCourse' }),
            component: (
                <EditItem
                    iconName='courseTemplate'
                    isClearable
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/exlCourseFront/getCourseSuggestions')}
                    exlCallBack={setTemplate}
                />
            ),
        },
        {
            propName: 'name',
            label: t(COURSE_MODALS_NAME_LABEL),
            validate: (v) => (!v || !v.length ? t(COURSE_MODALS_NO_NAME_VALI) : v.length > 60 ? t(COURSE_MODALS_TO_LONG_NAME_VALI) : ''),
            component: <EditText />,

        },
        {
            propName: 'teachers',
            label: t(COURSE_MODALS_TEACHER_LABEL),
            validate: (v) => (!v || v.length < 1 ? t(COURSE_MODALS_NO_TEACHER_VALI) : ''),
            component:
                <ExlEditItemList
                    iconName='teacher'
                    isClearable
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/exlTeacherFront/getTeacherSuggestions')}

                />
        },
        {
            propName: 'description',
            label: t(COURSE_MODALS_DESCRIPTION_LABEL),
            component: <ExlEditDraft />
        },
        {
            propName: 'maxNumBookings',
            label: t(COURSE_MODALS_MAX_BOOKINGS_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_MAX_BOOK_VALI)
                : v > 250 ? t(COURSE_MODALS_TO_HIGH_MAX_BOOK_VALI)
                    : v <= 0 ? t(COURSE_MODALS_TO_LOW_MAX_BOOK_VALI)
                        : ''),
            component: <ExlEditNumber placeholder={0} />,
        },
        {
            propName: 'maxNumReserves',
            label: t(COURSE_MODALS_MAX_RESERVES_LABEL),
            validate: (v) => (v > 250 ? t(COURSE_MODALS_TO_HIGH_MAX_RES_VALI) : ''),
            component: <ExlEditNumber placeholder={0} />,
        },
        {
            propName: 'status',
            label: t(COURSE_MODALS_STATUS_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_STATUS_VALI) : ''),
            component: <EditSelect options={courseStatuses} iconName={'chevron-down'} />
        },
        {
            propName: 'category',
            label: t(COURSE_MODALS_CATEGORY_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_CATE_VALI) : ''),
            component: <EditSelect options={courseCategories} iconName={'chevron-down'} />
        },
        {
            propName: 'language',
            label: t(COURSE_MODALS_LANG_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_LANG_VALI) : ''),
            component: <EditSelect options={courseLanguages} iconName={'chevron-down'} />
        },
        {
            propName: 'courseFormat',
            label: t(COURSE_MODALS_FORMAT_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_FORMAT_VALI) : ''),
            component: <EditSelect options={courseFormats} iconName={'chevron-down'} />
        },
        {
            propName: 'location',
            label: t(COURSE_MODALS_ADDRESS_LABEL),
            validate: (v) => (!v || !v.length ? t(COURSE_MODALS_NO_ADDRESS_VALI) : ''),
            component: <EditText />
        },
        {
            propName: 'meetingLink',
            label: t(COURSE_MODALS_MEET_LINK_LABEL),
            component: <EditText />
        },
        {
            propName: 'price',
            propType: 'economy',
            label: t(COURSE_MODALS_PRICE_LABEL),
            component: < ExlEditNumber placeholder={0} />,
        },
        {
            propName: 'travelSupplement',
            propType: 'economy',
            label: t(COURSE_MODALS_TRAVEL_SUPPLEMENT_LABEL),
            component: <ExlEditNumber placeholder={0} />,
        },
        {
            propName: 'sendEmailNotifications',
            label: '',
            component:
                    <Checkbox
                        usedInExl={true}
                        label={t(COURSE_MODALS_EMAIL_NOTIF_LABEL)}
                    />
        },
    ];


    const filterFormComponents = (formValues) => {
        const filteredFormComponents= formComponents.filter((component) => {
            const allowedCourseFormatComp = courseFormatComponentsFilter(formValues?.courseFormat?.value, component);
            const allowedCourseCategoryComp = courseCategoryComponentsFilter(formValues?.category?.value, component);
            return (allowedCourseFormatComp && allowedCourseCategoryComp);
        });
        return filteredFormComponents;
    }

    const handleAdd = async (course) => {
        course.status = course.status.value;
        course.category = course.category.value;
        course.language = course.language.value;
        course.courseFormat = course.courseFormat.value;
        if (course.maxNumReserves === "") {
            delete course.maxNumReserves;
        }

        courseDeterminePriceAndTravelSupplement(course);

        switch (course.courseFormat) {
            case 0:
                course.location = null;
                break;
            case 1:
                course.meetingLink = null;
                break;
        }

        const response = await HandleAddCourse(course);

        course = response.data; //copys new course value to get the new course.id 

        return response;
    };

    return (
        <ExlCourseFormModal
            label={label}
            description={t(ADD_COURSE_LABEL, {ns: 'addCourse'})}
            defaultFormValues={defaultTemplate}
            defaultFormComponents={formComponents}
            callback={filterFormComponents}
            handleAdd={handleAdd}
            onSuccessiveSave={async (response) => {
                clearForm();
                navigate('/exlCourseDetails/' + response.data.id);
            }}
            isExl
            handleCancel={clearForm}
            trueText={t(EMAIL_NOTIFICATIONS_TRUE, {ns: 'shared'})}
            falseText={t(EMAIL_NOTIFICATIONS_FALSE, { ns: 'shared' })}
            tooltipPlace={'left'}
        />
    );
};
export default AddCourse;
